import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { ListingsApiData } from '../service/models/biListings'

// Generic table state interface
export interface TableState<T> {
	data: T | null
	loading: boolean
	error: boolean
	errorMessage?: string
	selectedStatus: string | null
	rowsPerPage: number
	paginationModel: GridPaginationModel
	sortModel: GridSortModel
	totalCount?: number
}

// Generic action types
export type TableAction<T> =
	| { type: 'SET_DATA'; payload: T | null }
	| { type: 'SET_LOADING'; payload: boolean }
	| { type: 'SET_ERROR'; payload: { error: boolean; errorMessage?: string } }
	| { type: 'SET_SELECTED_STATUS'; payload: string | null }
	| { type: 'SET_ROWS_PER_PAGE'; payload: number }
	| { type: 'SET_PAGINATION_MODEL'; payload: GridPaginationModel }
	| { type: 'SET_SORT_MODEL'; payload: GridSortModel }
	| { type: 'SET_TOTAL_COUNT'; payload: number }

export interface TableActions {
	setData: (data: ListingsApiData | null) => void
	setLoading: (loading: boolean) => void
	setError: (error: boolean, errorMessage?: string) => void
	setSelectedStatus: (status: string | null) => void
	setRowsPerPage: (rowsPerPage: number) => void
	setPaginationModel: (model: GridPaginationModel) => void
	setSortModel: (model: GridSortModel) => void
	handleSortModelChange: (model: GridSortModel) => void
	setTotalCount?: (count: number) => void
}

// Generic initial state factory
export function createInitialState<T>(): TableState<T> {
	return {
		data: null,
		loading: false,
		error: false,
		errorMessage: undefined,
		selectedStatus: 'All',
		rowsPerPage: 10,
		paginationModel: {
			page: 1,
			pageSize: 10,
		},
		sortModel: [],
		totalCount: 0,
	}
}

// Generic reducer factory
export function createTableReducer<T>() {
	return (state: TableState<T>, action: TableAction<T>): TableState<T> => {
		switch (action.type) {
			case 'SET_DATA':
				return {
					...state,
					data: action.payload,
					error: false,
					errorMessage: undefined,
				}
			case 'SET_LOADING':
				return { ...state, loading: action.payload }
			case 'SET_ERROR':
				return {
					...state,
					error: action.payload.error,
					errorMessage: action.payload.errorMessage,
					loading: false,
				}
			case 'SET_SELECTED_STATUS':
				return { ...state, selectedStatus: action.payload }
			case 'SET_ROWS_PER_PAGE':
				return {
					...state,
					rowsPerPage: action.payload,
					paginationModel: {
						...state.paginationModel,
						pageSize: action.payload,
					},
				}
			case 'SET_PAGINATION_MODEL':
				return { ...state, paginationModel: action.payload }
			case 'SET_SORT_MODEL':
				return { ...state, sortModel: action.payload }
			case 'SET_TOTAL_COUNT':
				return { ...state, totalCount: action.payload }
			default:
				return state
		}
	}
}

// Simple function to create sort parameter from field and direction
export const createSortParam = (
	field: string,
	direction: 'asc' | 'desc'
): string => {
	const prefix = direction === 'asc' ? '+' : '-'
	return `${prefix}${field}`
}
